import {
  bleedPage,
  columns,
  contactColumn,
  FlowTemplate,
  leadIcon,
  padHorizontal,
  photoWidget,
  pill,
  sectionIcon,
  type RenderContext,
  type Zone,
  type ZoneColors,
} from "../base";
import { box, center, col, keep, rect, text, vspace, wrapBox, type Node, type PageSpec } from "../ir";
import { initials, sidebarFriendly, type RenderSection } from "../model";
import { hex, Pal, WHITE } from "../theme";

const SIDE_PAD_L = 22;
const SIDE_PAD_R = 18;
const MAIN_PAD_L = 26;
const MAIN_PAD_R = 32;

/**
 * Meridian — a deep-coloured sidebar (photo, contact, skills, languages)
 * beside a roomy main column. Both columns continue onto extra pages.
 * (meridian_template.dart)
 */
export class MeridianTemplate extends FlowTemplate {
  readonly info = {
    id: "meridian",
    name: "Meridian",
    tagline: "Bold sidebar with photo — confident and polished",
    category: "modern" as const,
    layout: "Sidebar",
    defaultAccent: 0xff0f4c5c,
    atsFriendly: false,
    supportsPhoto: true,
  };

  readonly fonts = { body: "lato" as const };

  private sidebarWidth(c: RenderContext): number {
    return c.format.width * 0.33;
  }

  zoneWidth(c: RenderContext, z: Zone): number {
    const side = this.sidebarWidth(c);
    return z === "main" ? c.format.width - side - MAIN_PAD_L - MAIN_PAD_R : side - SIDE_PAD_L - SIDE_PAD_R;
  }

  private band(c: RenderContext) {
    return Pal.deep(c.t.accent);
  }

  colors(c: RenderContext, z: Zone): ZoneColors {
    if (z === "main") return super.colors(c, z);
    const band = this.band(c);
    return {
      text: Pal.mix(band, WHITE, 0.84),
      strong: WHITE,
      muted: Pal.mix(band, WHITE, 0.62),
      accent: Pal.mix(c.t.accent, WHITE, 0.55),
      line: Pal.mix(band, WHITE, 0.22),
    };
  }

  heading(c: RenderContext, s: RenderSection, z: Zone): Node {
    return this.titled(c, s.title, z, s);
  }

  private titled(c: RenderContext, title: string, z: Zone, s?: RenderSection): Node {
    const t = c.t;
    const col_ = this.colors(c, z);
    return col([
      leadIcon(
        s ? sectionIcon(c, s, z === "main" ? t.accentText : col_.accent, t.fs(z === "main" ? 11.5 : 10)) : null,
        text(
          title.toUpperCase(),
          t.style(t.heading.bold, z === "main" ? 10.4 : 9, { color: col_.strong, letterSpacing: 1.6, lineSpacing: 0 })
        )
      ),
      vspace(4),
      rect({ width: 24, height: 1.8, color: z === "main" ? t.accent : col_.accent }),
    ]);
  }

  chips(c: RenderContext, items: string[], z: Zone): Node {
    if (z === "main") return super.chips(c, items, z);
    const col_ = this.colors(c, z);
    return wrapBox(
      items.map((item) =>
        pill(item, c.t.style(c.t.body.regular, 8.1, { color: col_.strong, lineSpacing: 0 }), { border: col_.line, hPad: 6, vPad: 2.6 })
      ),
      { spacing: 4, runSpacing: 4 }
    );
  }

  build(c: RenderContext): PageSpec {
    const t = c.t;
    const m = c.model;
    const sideW = this.sidebarWidth(c);
    const side = this.colors(c, "sidebar");
    const sidePad = (n: Node) => padHorizontal(n, SIDE_PAD_L, SIDE_PAD_R);
    const mainPad = (n: Node) => padHorizontal(n, MAIN_PAD_L, MAIN_PAD_R);

    // The photo, or the initials in a ring when there is none.
    const photo =
      photoWidget(c, 92, { ring: Pal.mix(this.band(c), WHITE, 0.3), ringWidth: 2.5 }) ??
      (initials(m)
        ? box(
            { width: 82, height: 82, radius: 41, border: { width: 1.4, color: hex(side.accent) }, align: "center", justify: "center" },
            [text(initials(m), t.style(t.display.bold, 24, { color: WHITE, letterSpacing: 1, lineSpacing: 0 }))]
          )
        : null);

    const sidebarSections = m.sections.filter(sidebarFriendly);
    const mainSections = m.sections.filter((s) => !sidebarFriendly(s));

    const sidebar: Node[] = [
      ...(photo ? [sidePad(center(photo)), vspace(t.gap(22))] : []),
      ...(m.contacts.length
        ? [
            sidePad(
              keep([
                this.titled(c, "Contact", "sidebar"),
                vspace(this.headingGap(c, "sidebar")),
                ...contactColumn(c, {
                  style: t.style(t.body.regular, 8.2, { color: side.text, lineSpacing: 0 }),
                  iconColor: side.accent,
                  gap: 6,
                }),
                vspace(this.sectionGap(c, "sidebar")),
              ])
            ),
          ]
        : []),
      ...this.allSections(c, sidebarSections, "sidebar").map(sidePad),
    ];

    const main: Node[] = [
      mainPad(
        keep([
          m.name && text(m.name, t.style(t.display.bold, 26, { color: t.ink, letterSpacing: -0.2, lineSpacing: 0 })),
          m.headline && vspace(5),
          m.headline &&
            text(m.headline.toUpperCase(), t.style(t.body.bold, 9.6, { color: t.accentText, letterSpacing: 1.8, lineSpacing: 1 })),
          vspace(t.gap(22)),
        ])
      ),
      ...this.allSections(c, mainSections).map(mainPad),
    ];

    return bleedPage(c, {
      top: 38,
      bottom: 30,
      bands: [{ side: "left", width: sideW, color: hex(this.band(c)) }],
      footerRight: MAIN_PAD_R,
      footerBottom: 12,
      columns: columns({ left: sidebar, right: main, leftWidth: sideW }),
    });
  }
}
